import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";

import MOBILE from "../mobileConfig";
import useCssVarHeight from "../useCssVarHeight";
import useVisualViewport from "../useVisualViewport";
import useDragScroll from "../useDragScroll";
import { useScrollLock } from "../scrollLock";
import MobileApptStrip from "../components/MobileApptStrip";
import MobileActionBar from "../components/MobileActionBar";
import MobileRxAddSheet from "../components/MobileRxAddSheet";
import "../styles/mobile.scss";

/**
 * Create Rx on a phone.
 *
 * `host` is the SAME `.rx-host` tree the desktop screen renders — both panes,
 * every card, the jump tabs, all of it — handed over rather than rebuilt. This
 * file is the frame around it: pinned rows above, an action bar below, and the
 * add sheet that replaces the trip to the patient record.
 *
 *   row 1  back · Review prescription        pinned
 *   row 2  when + status                     pinned, not tappable
 *   row 3  the jump tabs (inside `host`)     pinned
 *   row 4  the card stack (inside `host`)    scrolls
 *   row 5  Preview | Create Rx               fixed
 *
 * ROW 1 IS DRAWN HERE EVEN THOUGH `host` HAS ONE. The pane's own `.rx-cap`
 * carries the same back button and the same title, but it sits inside
 * `.rx-pane-head` as a SIBLING of the tabs — so row 2 cannot be placed between
 * them without editing that tree. Drawing rows 1 and 2 out here and hiding
 * `.rx-cap` in CSS gets the board's order with no change to the desktop JSX.
 */

/** Which of the two views the screen is showing. */
const EDIT = "edit";
const PREVIEW = "preview";

const BackIcon = () => (
  <svg viewBox="0 0 16 16" aria-hidden="true" focusable="false">
    <path
      d="M10 2L4 8l6 6"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const MobilePrescriptionScreen = ({
  host,
  overlays,
  loading,
  error,
  ready,
  appointment,
  patient,
  appointmentId,
  updating,
  onChangeStatus,
  onBack,
  onBackToDashboard,
  saving,
  onPublish,
  instructionsPending,
  patientDetailsOpen,
}) => {
  const [view, setView] = useState(EDIT);

  /* PUBLISH IS REFUSED IN THE BUILDER, NOT THE PREVIEW. `onPublish` is the
     desktop's requestPublish: while a custom instruction is typed but not
     added (or too long) it shows an error and jumps to the instruction card
     instead of publishing. Publish lives in the preview, where that card is
     hidden, so a refusal goes back to the builder first and calls it once the
     builder has rendered — its error and its jump then land on a card the
     doctor can see. Same predicate requestPublish tests. */
  const refuseAfterEdit = useRef(false);
  const publish = () => {
    const p = (instructionsPending && instructionsPending.current) || {};
    if (p.unadded || p.tooLong) {
      refuseAfterEdit.current = true;
      setView(EDIT);
      return;
    }
    onPublish();
  };
  useEffect(() => {
    if (view !== EDIT || !refuseAfterEdit.current) return;
    refuseAfterEdit.current = false;
    onPublish();
  }, [view, onPublish]);

  useVisualViewport();
  useDragScroll();
  /* Patient details ("Update") arrives in `overlays` and locks nothing; <html>
     is what scrolls here, so without this a drag on it moved the builder
     behind. The host says when it is up — the dialog is its state, not ours. */
  useScrollLock(MOBILE.patientDetailsScrollLock && !!patientDetailsOpen);

  const row1Ref = useCssVarHeight("--zym-row1-h", [ready]);
  const row2Ref = useCssVarHeight("--zym-row2-h", [appointment && appointment.id, ready]);

  const previewing = view === PREVIEW;
  /* Row 2 and the space the rows below it leave for row 2 are one decision, so
     they are taken from one flag: the class zeroes `--zym-row2-h` for this
     screen, which is what the pinned offsets are chained through. */
  const showAppt = MOBILE.rxAppointmentStrip;

  return (
    <div
      className={`zym-rx${previewing ? " is-previewing" : ""}${
        showAppt ? "" : " is-no-appt"
      }`}
    >
      <div className="zym-head" ref={row1Ref}>
        <div className="zym-topbar">
          <button
            type="button"
            className="zym-icon-btn"
            onClick={previewing ? () => setView(EDIT) : onBack}
            aria-label="Back"
          >
            <BackIcon />
          </button>
          <h1 className="zym-topbar-title">
            {previewing ? "Document preview" : "Review prescription"}
          </h1>
        </div>
      </div>

      {ready && showAppt && (
        <div className="zym-appt-wrap" ref={row2Ref}>
          {/* READ-ONLY, and no detail sheet behind it.

              No sheet, because this screen never loads the touchpoints,
              documents or reason list the patient record's sheet is built from
              — that fetch was deliberately dropped here.

              No status dropdown, because changing the status on THIS screen is
              quietly destructive: the seed effect depends on the `appointment`
              object, useStatusChange hands it a new identity, and the re-seed's
              tail replaces `picks` with the freshly derived "new today" set —
              so every box ticked by hand comes undone mid-prescription. The
              control belongs on the patient record, where there is no builder
              to lose. */}
          <MobileApptStrip
            appointment={appointment}
            updating={updating}
            onChangeStatus={onChangeStatus}
            readOnly
          />
        </div>
      )}

      <div className="zym-rx-body">
        {loading && <div className="zym-message">Loading…</div>}

        {!loading && error && (
          <div className="zym-message is-error">
            <p>{error}</p>
            <button type="button" className="zym-btn is-primary" onClick={onBackToDashboard}>
              Back to appointments
            </button>
          </div>
        )}

        {/* Rows 3 and 4 both live in here. Preview swaps which pane shows; the
            tree is the same either way, so nothing is torn down and the
            builder is exactly as it was on the way back. */}
        {host}
      </div>

      {/* PUBLISHING LIVES IN THE PREVIEW, NOT BESIDE THE BUILDER.

          The builder view offers one thing — read the document — and the
          document view is where it can be signed off. A prescription is a legal
          record; putting Publish next to the fields makes it possible to issue
          one without ever having read it back, and the two buttons sitting side
          by side invited exactly that.

          "Publish Rx" rather than "Create Rx" once you are there, which is both
          what the action does and what the desktop header calls it. */}
      {ready && (
        <MobileActionBar>
          {previewing ? (
            <>
              <button type="button" className="zym-btn" onClick={() => setView(EDIT)}>
                Back to edit
              </button>
              <button
                type="button"
                className="zym-btn is-primary"
                disabled={saving}
                onClick={saving ? undefined : publish}
              >
                {saving ? "Publishing…" : "Publish Rx"}
              </button>
            </>
          ) : (
            <button
              type="button"
              className="zym-btn is-outline is-wide"
              onClick={() => setView(PREVIEW)}
            >
              Preview
            </button>
          )}
        </MobileActionBar>
      )}

      {ready && <MobileRxAddSheet patient={patient} appointmentId={appointmentId} />}

      {overlays}
    </div>
  );
};

MobilePrescriptionScreen.propTypes = {
  /** The desktop `.rx-host` tree, handed over whole. */
  host: PropTypes.node,
  /** Remove-medicine confirm, the cancel/refund dialogs, the publish dialog. */
  overlays: PropTypes.node,
  loading: PropTypes.bool,
  error: PropTypes.string,
  ready: PropTypes.any,
  appointment: PropTypes.object,
  patient: PropTypes.object,
  appointmentId: PropTypes.number,
  updating: PropTypes.bool,
  onChangeStatus: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired,
  onBackToDashboard: PropTypes.func.isRequired,
  saving: PropTypes.bool,
  onPublish: PropTypes.func.isRequired,
  /** requestPublish's ref: { unadded, tooLong } custom-instruction boxes. */
  instructionsPending: PropTypes.shape({ current: PropTypes.object }),
  /** Whether the Patient details dialog in `overlays` is open — holds the scroll lock. */
  patientDetailsOpen: PropTypes.bool,
};

export default MobilePrescriptionScreen;
